<template>
  <Teleport to="body">
    <transition name="modal-fade">
      <div v-if="show" class="modal-backdrop-custom" @mousedown.self="!persistent && close()">
        <div
          ref="dialog"
          class="modal-panel"
          :class="`modal-panel--${size}`"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          tabindex="-1"
          @keydown.esc="!persistent && close()"
        >
          <header class="modal-panel__header">
            <div class="d-flex align-items-center gap-2">
              <i v-if="icon" :class="icon" class="modal-panel__icon" aria-hidden="true"></i>
              <h2 :id="titleId">{{ title }}</h2>
            </div>
            <button type="button" class="btn-close" aria-label="Fermer" @click="close"></button>
          </header>
          <div class="modal-panel__body">
            <slot />
          </div>
          <footer v-if="$slots.footer" class="modal-panel__footer">
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { nextTick, ref, watch, onBeforeUnmount } from "vue";

const props = defineProps({
  show: { type: Boolean, default: false },
  title: { type: String, default: "" },
  icon: { type: String, default: "" },
  size: { type: String, default: "md" },
  persistent: { type: Boolean, default: false },
});

const emit = defineEmits(["update:show", "close"]);
const dialog = ref(null);
const titleId = `modal-title-${Math.random().toString(36).slice(2, 9)}`;
let previousFocus = null;

const close = () => {
  emit("update:show", false);
  emit("close");
};

/** Modales ouvertes (superposées) : le défilement de la page reste bloqué tant qu'il en reste une. */
const lockScroll = (lock) => {
  const open = Number(document.body.dataset.openModals || 0) + (lock ? 1 : -1);
  document.body.dataset.openModals = String(Math.max(0, open));
  document.body.classList.toggle("modal-open-custom", open > 0);
};
let locked = false;

watch(
  () => props.show,
  async (visible) => {
    if (visible !== locked) {
      locked = visible;
      lockScroll(visible);
    }
    if (visible) {
      previousFocus = document.activeElement;
      await nextTick();
      const target = dialog.value?.querySelector("input, select, textarea, button:not(.btn-close)") || dialog.value;
      target?.focus();
    } else if (previousFocus && typeof previousFocus.focus === "function") {
      previousFocus.focus();
    }
  }
);

onBeforeUnmount(() => {
  if (locked) lockScroll(false);
});
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgb(15 23 42 / 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 4vh 1rem;
  overflow-y: auto;
}
.modal-panel {
  width: 100%;
  background: hsl(var(--card));
  border-radius: calc(var(--radius) + 4px);
  box-shadow: 0 24px 48px rgb(0 0 0 / 0.18);
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  outline: none;
}
.modal-panel--sm { max-width: 420px; }
.modal-panel--md { max-width: 620px; }
.modal-panel--lg { max-width: 880px; }
.modal-panel--xl { max-width: 1140px; }
.modal-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid hsl(var(--border));
}
.modal-panel__header h2 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
}
.modal-panel__icon {
  color: hsl(var(--primary));
}
.modal-panel__body {
  padding: 1.25rem;
  overflow-y: auto;
  text-align: left;
}
.modal-panel__footer {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.875rem 1.25rem;
  border-top: 1px solid hsl(var(--border));
  background: hsl(var(--muted) / 0.4);
  border-radius: 0 0 calc(var(--radius) + 4px) calc(var(--radius) + 4px);
}
.modal-panel__footer > :only-child {
  margin-left: auto;
}
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.18s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
